/**
 * Sprache und Formate eines Vertrags. Ein englischer Vertrag wird durchgehend englisch formatiert (en-GB), unabhängig
 * von der Sprache der Agentur; ein deutscher behält die regionale Form der Agentur (de-DE, de-AT, de-CH).
 * Dazu der Rückfall für Firmenangaben, die eine Agentur nur auf Deutsch gepflegt hat (Rechtswahl, Funktion, Gerichtsstand).
 */
import type { Language, MoneyLocale } from "./types.js";

const GERMAN_LOCALES: readonly MoneyLocale[] = ["de-DE", "de-AT", "de-CH"];

/** Formatierung eines Vertrags aus seiner Sprache (Befund 8): englisch immer en-GB, deutsch in der Form der Agentur. */
export function contractLocale(language: Language, orgLocale?: string | null): MoneyLocale {
  if (language === "en") return "en-GB";
  return (GERMAN_LOCALES as readonly string[]).includes(orgLocale ?? "") ? (orgLocale as MoneyLocale) : "de-DE";
}

/** Wirkt ein Wert deutsch (Umlaute oder typische Rechtsbegriffe)? Für den Hinweis bei englischen Verträgen. */
export function looksGerman(value: string | null | undefined): boolean {
  if (!value) return false;
  return /[äöüß]|\b(recht|rechts|deutsch|gericht|geschäftsf|geschaeftsf|inhaber|prokur|gesellschaft|vorstand|amtsgericht|landgericht)/i.test(value);
}

/**
 * K-532: Sprache eines eingefügten Freitexts grob erkennen (häufige Funktionswörter und Umlaute), damit die Prüfung vor dem
 * Versand englische Textstellen in einem deutschen Vertrag (und umgekehrt) nennen kann. Kurze oder unklare Texte: null.
 */
const DE_WOERTER = new Set(["und", "der", "die", "das", "mit", "für", "auf", "nicht", "ist", "von", "den", "dem", "des", "im", "zu", "zum", "zur", "ein", "eine", "einen", "oder", "auch", "wird", "werden", "nach", "bei", "sowie", "über", "sind", "kanälen", "nutzung", "monate", "monaten", "ab"]);
const EN_WOERTER = new Set(["and", "the", "with", "for", "on", "not", "is", "of", "to", "in", "a", "an", "or", "also", "will", "be", "by", "from", "as", "at", "use", "usage", "channels", "website", "organic", "months", "month", "rights", "paid", "posts", "brand", "social"]);
export function textLanguage(value: string | null | undefined): "de" | "en" | null {
  if (!value) return null;
  const woerter = value.toLowerCase().match(/[a-zäöüß]+/g) ?? [];
  if (woerter.length < 3) return null;
  let de = /[äöüß]/i.test(value) ? 2 : 0;
  let en = 0;
  for (const w of woerter) { if (DE_WOERTER.has(w)) de += 1; if (EN_WOERTER.has(w)) en += 1; }
  if (en >= 2 && en > de * 2) return "en";
  if (de >= 2 && de > en * 2) return "de";
  return null;
}

type LegalKind = "governing_law" | "jurisdiction" | "position";

const LAW: readonly [RegExp, string][] = [
  [/(bundesrepublik\s+)?deutschland|deutsch(es|em)?\s+recht/i, "the laws of the Federal Republic of Germany"],
  [/österreich|oesterreich/i, "the laws of the Republic of Austria"],
  [/schweiz|schweizer/i, "the laws of Switzerland"],
  [/england|englisch/i, "the laws of England and Wales"],
];
const POSITION: readonly [RegExp, string][] = [
  [/geschäftsführ|geschaeftsfuehr|geschäftsleitung/i, "Managing Director"],
  [/inhaber/i, "Owner"],
  [/prokurist/i, "Authorised Officer (Prokurist)"],
  [/vorstand/i, "Member of the Management Board"],
  [/gesellschafter/i, "Partner"],
];
const CITY: Readonly<Record<string, string>> = {
  münchen: "Munich", koeln: "Cologne", köln: "Cologne", wien: "Vienna", zürich: "Zurich", zuerich: "Zurich", genf: "Geneva",
  nürnberg: "Nuremberg", nuernberg: "Nuremberg", braunschweig: "Brunswick", hannover: "Hanover",
};

/**
 * Deutsche Firmenangaben für englische Verträge übertragen, wenn die Agentur keine englische Fassung hinterlegt hat.
 * Liefert null, wenn kein sicherer Ersatz bekannt ist (dann bleibt der Wert und die Prüfung weist darauf hin).
 */
export function englishLegalTerm(kind: LegalKind, value: string | null | undefined): string | null {
  const v = (value ?? "").trim();
  if (!v) return null;
  if (!looksGerman(v) && kind !== "jurisdiction") return v;
  if (kind === "governing_law") {
    const hit = LAW.find(([rx]) => rx.test(v));
    if (!hit) return null;
    return /un-?kaufrecht|cisg/i.test(v) ? `${hit[1]}, excluding the UN Convention on Contracts for the International Sale of Goods` : hit[1];
  }
  if (kind === "position") return POSITION.find(([rx]) => rx.test(v))?.[1] ?? null;
  // Gerichtsstand: Ortsnamen übertragen, „Amtsgericht“/„Landgericht“ bleiben als Eigenname stehen
  const city = CITY[v.toLowerCase()];
  if (city) return city;
  return looksGerman(v) && !/^[A-ZÄÖÜ][\p{L}.\- ]+$/u.test(v) ? null : v;
}
